import { IonHeader, IonIcon, IonSpinner } from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";
import Page from "../components/Page";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import * as DishActions from "../actions/DishActions";
import { chevronBackOutline, chevronUpOutline, removeOutline } from "ionicons/icons";
import withNav from "../utils/hoc/withNav";
import OrderService from "../services/OrderService";
import { setCart } from "../actions/CartActions";
import { deepCopy, isEmpty, isEmptyObject } from "../utils/objects";
import { DISH_MODES } from "../constants/DishConstants";
import ToastService from "../services/ToastService";
import {
	Modal,
	ModalSubtitle,
	ModalButtonGroup,
	ModalPrimaryButton,
	ModalSecondaryButton,
	ModalOverlay,
	ModalHeader,
	ModalNote,
} from "../components/Modal";
import { debounce, uptilTwoDecimalPlaces } from "../utils/misc";
import { calculateInventory, validateCartItems } from "../utils/parsers/inventory";
import { updateIngrdientQuantity } from "../actions/InventoryActions";
import { INVENTORY_UPDATE_ACTION } from "../constants/InventoryConstants";
import Analytics, { TRACK_PAGE_CATEGORIES } from "../lib/anayltics";
import FoodService from "../services/FoodService";
import BottomCTA from "../components/BottomCTA";

const Center = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 100%;
	width: 100%;
`;

//TODO: Add this to the Page component.
const PageHeader = styled(IonHeader)`
	position: sticky;
	top: 0;
	background: ${({ theme }) => theme.colors.main.background};
	color: #f2f2f2;
	font-style: normal;
	font-size: 20px;
	display: flex;
	align-items: center;
	padding: 1rem;
	border: none;
	box-shadow: none;
	&:after {
		background-image: none;
	}
`;

const BackButton = styled.div`
	margin: 2px 0.5rem 0 -5px;
`;

const BackButtonImg = styled(IonIcon)`
	width: 24px;
	height: 24px;
	color: #fff;
`;

const PageTitle = styled.div`
	font-style: normal;
	font-weight: 500;
	font-size: 20px;
	letter-spacing: 0px;
	display: flex;
	justify-content: center;
`;

const Header = styled.div<any>`
	text-align: left;
	font-style: normal;
	font-weight: 500;
	font-size: 19px;
	color: ${(props) => props.color || "#f2f2f2"};
	max-width: ${(props) => props.maxWidth};
`;

const SubHeader = styled.div`
	color: #f2f2f2;
	opacity: 0.6;
	font-weight: 400;
	font-size: 16px;
	text-align: left;
	font-style: normal;
`;

const Space = styled.div`
	margin: 1.5rem 0 0 0;
`;

const SegmentsSection = styled.div`
	padding: 0 1rem 6rem 1rem;
	> : nth-child(1) {
		margin: 0 0 1rem 0;
	};
`;

const Segment = styled.div<any>`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: max-content;
	margin: 1rem 0;
`;

const SegmentHead = styled.div`
	width: 100%;
	display: flex;
	align-items: baseline;
`;

const Arrow = styled(IonIcon)<any>`
	font-size: 23px;
	transition: all linear 300ms;
	margin-left: auto;
`;

const SegmentRules = styled.div<any>`
	display: flex;
	flex-direction: column;
	margin: 0.5rem 0 0.5rem 0;
`;

const SegmentRule = styled.div<any>`
	width: ${(props) => props.width || "80%"};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	margin: ${(props) => props.margin};
	text-align: left;
	font-weight: 400;
	font-size: 13px;
	height: 14px;
	color: ${(props) => (props.active ? "#FF0000" : "#D6D6D6;")};
`;

const SegmentItems = styled.div<any>`
	display: flex;
	flex-direction: row;
	overflow-x: scroll;
	width: 100%;
	gap: 1rem;
	margin-right: -16px;
`;

const SegmentSeperator = styled.div`
	border: 1px solid #939393;
`;

const IngredientStates = {
	SELECTED: "SELECTED",
	DEFAULT: "DEFAULT",
	SOLD_OUT: "SOLD_OUT",
	FILLER: "FILLER",
};

const IngredientCardColors = {
	[IngredientStates.SELECTED]: {
		background: "#FFFFFF",
		text: "#2A2A2A",
		fill: "#EDBE02",
		fillText: "#FFFFFF",
	},
	[IngredientStates.DEFAULT]: {
		background: "#A0A0A0",
		text: "#2A2A2A",
		fill: "",
		fillText: "",
	},
	[IngredientStates.SOLD_OUT]: {
		background: "#6A6969",
		text: "#2A2A2A",
		fill: "#F4A8A8",
		fillText: "#404040",
	},
	[IngredientStates.FILLER]: {
		background: "",
		text: "",
		fill: "",
		fillText: "",
	},
};

const IngredientCard = styled.div<any>`
	flex-grow: 1;
	flex-basis: 0;
	padding: 0.5rem 1rem;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	align-items: center;
	text-align: center;
	height: 145px;
	margin: ${(props) => props.margin};
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	border-radius: 4px;
	min-width: 110px;
	max-width: 110px;
`;

const IngredientTitle = styled.div`
	font-weight: 400;
	font-size: 13px;
	color: inherit;
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 2; /* number of lines to show */
	line-clamp: 2;
	-webkit-box-orient: vertical;
	max-width: 15ch;
	height: 28px;
`;

const IngrdientImg = styled.img<any>`
	margin: 0.3rem 0 0 0;
	width: 62.2px;
	height: 50.8px;
	filter: ${(props) => (props.isSoldOut ? "grayscale(100%)" : "")};
`;

const AllergenMessage = styled.div`
	color: #ca040c;
	margin: 2px 0 0 0;
	font-size: 10px;
`;

const SoldOutMessage = styled.div`
	color: rgb(245 118 118 / 76%);
	font-size: 12px;
	font-weight: 500;
`;

const IngredientFooter = styled.div`
	margin-top: auto;
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const IngredientIcon = styled(IonIcon)<any>`
	color: ${(props) => props.isSelected ? "#6F6F6F" : "#4B4B4B"};
	font-size: 16px;
	${(props) =>
		props.disabled && props.isSelected &&
		css`
			color: #D7D7D7
			pointer-events: none;
		`}

	${(props) =>
		props.disabled &&
		css`
			color: #A5A5A5
			pointer-events: none;
		`}
`;

const IngredientQuantity = styled.div<any>`
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	color: #000;
`;

const IngredientSelectIcon = styled.div<any>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: ${({ isSelected }) => isSelected ? "#6F6F6F" : "#4B4B4B"};
  
  ${({ disabled, isSelected }) => {
    if (disabled && isSelected) {
      return css`
        color: #D7D7D7;
        pointer-events: none;
      `;
    }
    
    if (disabled) {
      return css`
        color: #A5A5A5;
        pointer-events: none;
      `;
    }
  }}
`;
const StickyHeader = styled.div<any>`
	position: sticky;
	top: 61px;
	height: max-content;
	width: 100%;
	background: inherit;
	opacity: ${(props) => props.opacity};
	z-index: 111;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding: 0 1rem 1rem 1rem;
`;

const StickyHeaderTitle = styled.div`
	font-weight: 500;
	font-size: 20px;
	color: #f2f2f2;
`;

const StickyHeaderLine = styled.div`
	margin: 0.5rem 0 0 0;
	height: 3px;
	width: 35px;
	background: #ffcc00;
`;

const StickyHeaderFooter = styled.div`
	margin: 0.6rem 0 0 0;
	display: flex;
	align-items: center;
`;

const StickyHeaderPricing = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 25px;
`;

const StickyHeaderPrice = styled.div`
	font-weight: 500;
	font-size: 20px;
	color: #f2f2f2;
`;

const StickyHeaderDiscountedPrice = styled.div`
	font-weight: 600;
	font-size: 14px;
	color: #f2f2f2;
	text-decoration: line-through;
	margin: 0 0.5rem;
`;

const StickyDishNutritionDataWrapper = styled.div`
  	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 0.5rem;
`;

const NutritionDataContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 0.5rem;
	
	&:not(:last-child)::after {
		content: "•";
		font-size: 22px;
		line-height: 0;
		color: #999;
		display: inline-flex !important;
		align-self: center !important;
		font-size: 22px !important;
		line-height: 1 !important;
		vertical-align: middle !important;
		align-items: center !important;
		justify-content: center !important;
	}
`;

const NutritionTitle = styled.div`
  	font-weight: 400;
	font-size: 14px;
	color: #C0C0C0;
	line-height: 14px;
`;

const NutritionValue = styled.div`
  	font-weight: 600;
	font-size: 16px;
	color: #C0C0C0;
`
const Row = styled.div<any>`
	display: flex;
	justify-content: ${(props) => props.jContent || "space-between"};
	align-items: center;
	width: 100%;
	padding: ${(props) => props.padding};
	gap: ${(props) => props.gap};
	margin: ${(props) => props.margin || "0 0 0 0"};
`;

const cartAddDisabledError =
	"We regret to inform you that we currently do not have enough stock to fulfill your request. Please consider adjusting the quantity or try again later.";

const backupImageUrl =
	"https://res.cloudinary.com/xook/image/upload/v1707202618/images/ingredients/Cooked_butternut_squash_cubes_w5uku4.png";

const DishCustomizations = (props: any) => {
	//States from Store
	const { history, Xook, Food, Dish, User, Cart, Inventory } = props;

	const { maxItemsPerOrder, xookId, currency, pricingMap } = Xook;

	const { dishesDataDynamic, ingredientDetails, recipesMap, dishDetails, brandDetails, allergies } = Food;

	const { availableItems: ingredientsDynamicData, initialInventory } = Inventory;

	const { cartItems, itemsCount, offers, cartPrice } = Cart;

	const {
		quantity,
		dishData,
		totalCalories,
		totalProtein,
		totalFibre,
		quantity: dishQuantity,
		totalDishPrice,
		priceWithoutOffers,
		segments,
		segmentsNotFulfilled,
		currentDishInventory,
		cartIndex,
		mode,
		selectedAllergicIngredients,
		ingredients,
		showDefaultNotSatisfiedMessage,
		dishPricing,
		ingredientPricing,
		customisedPortions,
		pricingFailure,
	} = Dish;

	//Actions from the Store
	const { navigate, setDishData, clearDishData, changeIngredientQuantity, setCart, updateIngrdientQuantity, setMiscDishData } =
		props;

	const { brandId, dishId, kioskId } = props.match.params;
	const query = useQuery();
	const selectedMode = query.get("mode");
	//Refs to HTML elements for DOM manipulation.
	const pageRef = useRef<any>(null);

	//Component States
	const [loading, setLoading] = useState(false);
	const [priceBeingFetched, togglePriceBeingFetched] = useState(false);
	const [error, setError] = useState(false);
	const [actionLoading, setActionLoading] = useState(false);
	const [showErrorModal, toggleErrorModal] = useState(false);
	const [showAllergicModal, toggleAllergicModal] = useState(false);

	//Flag for stopping the user to add the dish to the cart.
	const allSegmentsFulfilled = Object.keys(segmentsNotFulfilled).length === 0;

	//Segment animations Config
	const segmentCloseClass = "segment-close";
	const segmentOpenClass = "segment-open";
	const arrowClass = "rotate-half";

	function onPageLoad() {
		Analytics.trackPage(TRACK_PAGE_CATEGORIES.PAGE_LOAD, "Dish Customisations");

		getDishData();
	}

	useEffect(() => {
		onPageLoad();
		return () => {
			clearDishData();
		};
	}, []);

	useEffect(() => {
		const filteredOutSegments = createSegmentsPayload(segments);

		if (!isEmptyObject(filteredOutSegments)) {
			togglePriceBeingFetched(true);
			debounce(() => {
				getUpdatedDishPrice(filteredOutSegments);
			}, 500);
		}
	}, [segments]);

	function useQuery() {
		return new URLSearchParams(useLocation().search);
	}

	function getDishData() {
		if (!(dishId || brandId)) {
			setError(true);
			return;
		}
		if (isEmpty(dishDetails) || isEmpty(dishesDataDynamic)) {
			navigate.push(`/kiosk/${kioskId}`);
			return;
		}
		const dishDataStatic = dishDetails?.[dishId];
		const dishDataDynamic = dishesDataDynamic?.[dishId];
		if (!(dishDataStatic && dishDataDynamic)) {
			setError(true);
			return;
		}
		const brandData = brandDetails?.[brandId];
		if (!brandData) {
			setError(true);
			return;
		}
		const { recipeId, dishPricingId, ingPricingId, originalPrice, offeredPrice } = dishDataDynamic;

		const hasDiscounts: boolean = typeof offeredPrice !== "undefined" && offeredPrice !== originalPrice;

		const recipeData = recipesMap?.[recipeId];
		if (!recipeData) {
			setError(true);
			return;
		}
		const dishData = { ...dishDataStatic, dynamicData: dishDataDynamic };
		const { calories, segments } = recipeData;
		let modeToUse = selectedMode || DISH_MODES.CREATE;

		const { dietaryPreferences } = User;
		const dishPricing = pricingMap[dishPricingId];
		const ingredientPricing = pricingMap[ingPricingId];

		const dishSelectPayload = {
			brandData,
			totalCalories: calories,
			ingredients: ingredientDetails,
			segments,
			availableItems: ingredientsDynamicData,
			dishData,
			mode: modeToUse,
			dietaryPreferences,
			allergies,
			dishPricing,
			ingredientPricing,
			initialInventory,
			totalDishPrice: hasDiscounts ? offeredPrice : originalPrice,
			priceWithoutOffers: hasDiscounts ? originalPrice : 0,
		};

		setDishData(dishSelectPayload);
	}

	function createSegmentsPayload(segments: any) {
		let requiredPayload: any = {};

		for (const segmentId in segments) {
			const segmentData = segments[segmentId];
			let { ingredients } = segmentData as any;

			for (const ingredientId in ingredients) {
				const ingredientData = ingredients[ingredientId];
				let { selectedQuantity } = ingredientData as any;

				//Take ingredients for whom quantity is positive.
				if (selectedQuantity) {
					//Add them to the cart Payload
					requiredPayload[segmentId] = {
						...requiredPayload[segmentId],
						ingredients: {
							...requiredPayload[segmentId]?.ingredients,
							[ingredientId]: {
								portionSelected: selectedQuantity,
							},
						},
					};
				}
			}
		}

		return requiredPayload;
	}

	async function getUpdatedDishPrice(segments: any) {
		const { offers, price } = dishData?.dynamicData || {};
		const payload = {
			ingPricing: ingredientPricing,
			dishPricing,
			cartItem: {
				quantity,
				offers,
				dishId,
				price,
				segments,
			},
		};
		const { data, error } = await FoodService.getUpdatedDishPrice(payload);
		if (data) {
			const { price, offeredPrice } = data;
			if (typeof offeredPrice !== "undefined" && offeredPrice !== price) {
				setMiscDishData({ priceWithoutOffers: price, totalDishPrice: offeredPrice, pricingFailure: false });
			} else {
				setMiscDishData({ totalDishPrice: price, pricingFailure: false });
			}
		}
		if (error) {
			ToastService.showToast(error);
			setMiscDishData({ pricingFailure: true });
		}
		togglePriceBeingFetched(false);
	}

	//Map and Render Dish Details.
	function renderDishDetails() {
		if (isEmpty(dishData)) {
			return;
		}
		const { title } = dishData;
		const hasDiscountedPrice: boolean = priceWithoutOffers && totalDishPrice !== priceWithoutOffers;
		return (
			<StickyHeader>
				<Row>
					<StickyHeaderTitle>{title}</StickyHeaderTitle>
					<StickyHeaderPricing>
						{priceBeingFetched ? (
							<IonSpinner name="dots" />
						) : (
							<>
								<StickyHeaderPrice>
									{currency} {uptilTwoDecimalPlaces(totalDishPrice)}
								</StickyHeaderPrice>
								{hasDiscountedPrice ? (
									<StickyHeaderDiscountedPrice>
										{currency} {uptilTwoDecimalPlaces(priceWithoutOffers)}
									</StickyHeaderDiscountedPrice>
								) : (
									""
								)}
							</>
						)}
					</StickyHeaderPricing>
				</Row>
				<StickyHeaderLine />
				<StickyHeaderFooter>
					<StickyDishNutritionDataWrapper>
						<NutritionDataContainer>
							<NutritionTitle>Calories</NutritionTitle>
							<NutritionValue>{totalCalories} kcal</NutritionValue>
						</NutritionDataContainer>
						<NutritionDataContainer>
							<NutritionTitle>Protein</NutritionTitle>
							<NutritionValue>{totalProtein} g</NutritionValue>
						</NutritionDataContainer>
						<NutritionDataContainer>
							<NutritionTitle>Fibre</NutritionTitle>
							<NutritionValue>{totalFibre} g</NutritionValue>
						</NutritionDataContainer>
					</StickyDishNutritionDataWrapper>
				</StickyHeaderFooter>

			</StickyHeader>
		);
	}

	function renderSegments() {
		if (!currentDishInventory || !segments) {
			setError(true);
			return;
		}
		//Holds all the JSX elements
		let renderData: any = [];
		//Parsing Every segment
		for (const segmentId in segments) {
			const segmentData = segments[segmentId];
			if (isEmpty(segmentData)) {
				console.warn(`Empty segment with segmentid ${segmentId}`);
				continue;
			}
			const {
				name,
				quantity,
				ingredients: segmentIngredients,
				maxPortionsReached,
				selectedIngredientsMessage,
			} = segmentData as any;
			const { minPortion, maxPortion } = quantity;
			let ingredientsRenderData: any = [],
				soldOutIngredientsRenderData: any = [];
			//Constraints on ingredient selection in the segments
			let ruleActive = "";
			//If segment is not fulfilled set the appropriate rule as active
			if (segmentId in segmentsNotFulfilled) {
				ruleActive = segmentsNotFulfilled[segmentId];
			}

			//Helps out to get the index, key and the value of the object
			for (const ingredientId in segmentIngredients) {
				let errorMessage = "";

				//Destructuring data from ingredient object
				const ingredientStaticData = ingredients[ingredientId];
				const ingredientDynamicData = ingredientsDynamicData[ingredientId];

				//Skip ingredient if insufficient data is found.
				if (!(ingredientStaticData && ingredientDynamicData)) {
					continue;
				}
				const { name, imageUrl: ingredeintImageUrl } = ingredientStaticData;

				const {
					selectedQuantity,
					quantity,
					amountPerPortion,
					isUserAllergic,
					ingredientAllergy,
					isSoldOut,
					readOnly,
					imageUrl: recipeImageUrl,
				} = segmentIngredients[ingredientId] as any;
				const imageUrl = ingredeintImageUrl || recipeImageUrl || backupImageUrl;
				const { minPortion: ingredientMinPortion, maxPortion: ingredientMaxPortion } = quantity;

				let disableRemove = false,
					disableAdd = false;

				//If the ingredient is sold out disable ingredient actions
				if (isSoldOut || readOnly) {
					disableAdd = true;
					disableRemove = true;
				}

				//Conditions for disabling ingredient removal
				//1: If the ingredient minimum portion limit is reached
				//                            OR
				//2: If the ingredient isn't mandatory and the quantity has reached 0;
				if (selectedQuantity === ingredientMinPortion || selectedQuantity === 0) disableRemove = true;

				//Conditions for disabling ingredient add
				//1: Quantity is equal to the max allowed Portion
				//              OR
				//2: Next quantity could be satisfied in the inventory

				if (maxPortionsReached) {
					disableAdd = true;
					errorMessage = "Max limit reached for this segment";
				}

				if (ingredientId in currentDishInventory && !disableAdd) {
					const neededForNextQuantity = amountPerPortion * dishQuantity;
					if (neededForNextQuantity > currentDishInventory[ingredientId].totalAvailableAmount) {
						disableAdd = true;
						errorMessage = "Can't satisfy enough quantity for this ingredient.";
					}
				}

				if (selectedQuantity === ingredientMaxPortion && !disableAdd) {
					disableAdd = true;
					errorMessage = "Max amount reached for this ingredient.";
				}

				//Function to increase the quantity of the ingredient on User Action
				const incrementIngredient = (e: any) => {
					e.stopPropagation();
					if (disableAdd) {
						return;
					}
					changeIngredientQuantity({
						ingredientId,
						segmentId,
						type: "INCREMENT",
						isUserAllergic,
					});
				};
				//Function to increase the quantity of the ingredient on User Action
				const decrementIngredient = (e: any) => {
					e.stopPropagation();
					if (disableRemove) return;
					changeIngredientQuantity({
						ingredientId,
						segmentId,
						type: "DECREMENT",
						isUserAllergic,
					});
				};
				//State of the ingredient commands the card styling
				let ingredientState: keyof typeof IngredientCardColors;

				//Setting the right states on the respective conditions
				if (isSoldOut) ingredientState = IngredientStates.SOLD_OUT;
				//Conditions for Selected state
				//1: If the ingredient has been selected or has some default amount preselected
				else if (selectedQuantity) ingredientState = IngredientStates.SELECTED;
				else ingredientState = IngredientStates.DEFAULT;

				//Get the styling for the particular state
				const { background, text, fill, fillText } = IngredientCardColors[ingredientState];
				//Filler dummy element to keep the layout intact
				const fillerDivElement = <div></div>;
				//Markup

				const Markup = (
					<IngredientCard
						key={ingredientId}
						background={background}
						color={text}
						onClick={(e: any) => {
							incrementIngredient(e);
						}}>
						<IngredientTitle>{name}</IngredientTitle>

						<IngrdientImg src={imageUrl || backupImageUrl} isSoldOut={isSoldOut} />
						{isUserAllergic && !isSoldOut && <AllergenMessage>Contains {ingredientAllergy}</AllergenMessage>}

						<IngredientFooter>
							{/* Conditional Rendering for the footer of the card  */}

							{!isSoldOut ? (
								<IngredientIcon
									isSelected={ingredientState === IngredientStates.SELECTED}
									disabled={disableRemove}
									onClick={(e: any) => {
										decrementIngredient(e);
									}}
									color={text}
									src={removeOutline}
								/>
							) : (
								fillerDivElement
							)}

							{!isSoldOut ? (
								<IngredientQuantity fill={fill} fillText={fillText}>
									{selectedQuantity}
								</IngredientQuantity>
							) : (
								""
							)}

							{isSoldOut ? <SoldOutMessage>SOLD OUT</SoldOutMessage> : ""}

							{!isSoldOut ? <IngredientSelectIcon isSelected={ingredientState === IngredientStates.SELECTED} disabled={disableAdd}>+</IngredientSelectIcon> : fillerDivElement}
						</IngredientFooter>
					</IngredientCard>
				);

				isSoldOut ? soldOutIngredientsRenderData.push(Markup) : ingredientsRenderData.push(Markup);
			}

			//Markup
			renderData.push(
				<>
					<Segment>
						<SegmentHead>
							<Header maxWidth="37%">{name}</Header>
							{ruleActive === "min" ? (
								<SegmentRule width="max-content" margin="0 0 0 0.5rem" active={true}>
									Minimum {minPortion} portion required.
								</SegmentRule>
							) : (
								""
							)}
							<Arrow
								id={`${segmentId}-arrow`}
								src={chevronUpOutline}
								onClick={() => {
									toggleSegment(segmentId);
								}}
							/>
						</SegmentHead>
						<SegmentRules>
							{selectedIngredientsMessage ? (
								<SegmentRule margin="0.5rem 0 0.2rem 0">
									{selectedIngredientsMessage.map((item: any, index: any, array: any) => {
										let string = item;
										if (index === array.length - 1) string += ".";
										else string += ", ";
										return string;
									})}
								</SegmentRule>
							) : (
								""
							)}
						</SegmentRules>
						<SegmentItems className={`${segmentOpenClass} no-scrollbar`} id={`${segmentId}-items`}>
							{ingredientsRenderData}
							{soldOutIngredientsRenderData}
						</SegmentItems>
					</Segment>
					<SegmentSeperator />
				</>
			);
		}
		return renderData;
	}

	function toggleSegment(segmentId: string) {
		//Get Elements
		const items = document.getElementById(`${segmentId}-items`);
		const arrow = document.getElementById(`${segmentId}-arrow`);
		//Check if both are rendered
		if (items && arrow) {
			//Toggle classes based on if the segment is hidden or not
			const hidden = items.classList.contains(segmentCloseClass);
			const itemsClassToAdd = hidden ? segmentOpenClass : segmentCloseClass;
			const itemsClassToRemove = hidden ? segmentCloseClass : segmentOpenClass;

			items.classList.remove(itemsClassToRemove);
			hidden && arrow.classList.remove(arrowClass);

			items.classList.add(itemsClassToAdd);
			!hidden && arrow.classList.add(arrowClass);
		}
	}

	//Handle Add to Cart Functionality
	function createAddToCartPayload() {
		const {
			_id,
			dynamicData: { recipeId },
		} = dishData;

		let filterOutSegmentData: any = {};
		//Set the portionSelected key in accordance with the quantity selected by the user.
		for (const segmentId in segments) {
			const segmentData = segments[segmentId];
			let { ingredients } = segmentData as any;

			for (const ingredientId in ingredients) {
				const ingredientData = ingredients[ingredientId];
				let { selectedQuantity } = ingredientData as any;

				//Take ingredients for whom quantity is positive.
				if (selectedQuantity) {
					//Add them to the cart Payload
					filterOutSegmentData[segmentId] = {
						...filterOutSegmentData[segmentId],
						ingredients: {
							...filterOutSegmentData[segmentId]?.ingredients,
							[ingredientId]: {
								portionSelected: selectedQuantity,
							},
						},
					};
				}
			}
		}

		if (isEmpty(filterOutSegmentData)) {
			throw new Error("Please Select some ingredients first!");
		}

		//CartItem Payload
		const dishPayload = {
			dishId: _id,
			title: dishDetails[_id].title,
			recipeId,
			quantity: dishQuantity,
			segments: filterOutSegmentData,
			totalCalories,
			totalProtein,
			totalFibre,
			price: priceWithoutOffers || totalDishPrice,
		};

		let newCartItems = deepCopy(cartItems);

		//Check if cartIndex is set in redux else just push at the back.
		if (cartIndex !== null) {
			newCartItems[cartIndex] = dishPayload;
		} else {
			newCartItems.push(dishPayload);
		}

		//Cart Payload
		let payload: any = {
			cart: {
				cartPrice,
				cartItems: newCartItems,
				itemsCount,
				offers,
			},
		};

		return payload;
	}

	function handleAddToCartClick() {
		//If allergic ingredients are selected show allergic modal.
		if (selectedAllergicIngredients.length) {
			toggleAllergicModal(true);
		} else {
			addToCart();
		}
	}

	async function addToCart() {
		let payload: any = {};

		if (mode === DISH_MODES.CREATE && itemsCount + 1 > maxItemsPerOrder) {
			ToastService.showToast("Max Cart Quantity Reached");
			return;
		}

		switch (mode) {
			case DISH_MODES.CREATE:
				//Check if it can be added to cart based on local inventory calculations.
				const createValidationResponse = validateCartItems({
					cartItems: [{ segments, recipeId: dishData.dynamicData.recipeId, quantity: 1 }],
					availableItems: ingredientsDynamicData,
					recipesMap,
				});

				if (!createValidationResponse.isCartValid) {
					ToastService.showToast(cartAddDisabledError);
					return;
				}

				break;

			case DISH_MODES.CUSTOMIZE:
				//Check if newly added customisations can be added to the cart.
				const customiseValidationResponse = validateCartItems({
					cartItems: [{ segments: customisedPortions, recipeId: dishData.dynamicData.recipeId, quantity: dishQuantity }],
					availableItems: ingredientsDynamicData,
					recipesMap,
				});

				if (!customiseValidationResponse.isCartValid) {
					ToastService.showToast(cartAddDisabledError);
					return;
				}

				break;

			default:
				break;
		}

		try {
			//Creating the payload for adding the dish to cart
			payload = createAddToCartPayload();
		} catch (error: any) {
			const errorMessage = error.message || "Something went wrong!";
			ToastService.showToast(errorMessage);
			return;
		}

		setActionLoading(true);
		//Sending the data to the server.
		const response = await OrderService.addToCart(payload, xookId);
		const { data, error } = response;
		setActionLoading(false);
		if (data) {
			const { newCart, discardedCart } = data;
			let showDiscardedItemsError = false;

			if (discardedCart?.cartItems?.length) {
				//Show Error
				showDiscardedItemsError = true;
			}

			//Validate Cart on every change
			const { isCartValid, invalidCartItems } = validateCartItems({
				cartItems: newCart.cartItems,
				recipesMap,
				availableItems: initialInventory,
			});

			//Filter out valid items
			const calculateOnlyFor = newCart.cartItems.filter((cartItem: any, index: any) => !invalidCartItems.includes(index + ""));
			//Calculate inventory for only valid items.
			const { quantityRequired } = calculateInventory({ cartPayload: { cartItems: calculateOnlyFor }, recipesMap });
			//Update inventory accordingly
			updateIngrdientQuantity({ inventoryPayload: quantityRequired, mode: INVENTORY_UPDATE_ACTION.REMOVE_FROM_INITIAL });

			//Set the updated cart in redux.
			setCart({ ...newCart, isCartValid, invalidCartItems, showDiscardedItemsError, discardedCart });
			//Send back to origin if the mode set was Customize.
			if (mode === DISH_MODES.CUSTOMIZE) {
				history.goBack();
			} else {
				navigate.push(`/kiosk/${xookId}/cart`, "forward");
			}
		}
		if (error) {
			//Show Error Message
			toggleErrorModal(true);
		}
	}

	function renderDefaultNotSatisfiedModal() {
		let preText =
			"Some default ingredients are not in sufficient quantity. Please feel free to add or replace them with other options.";
		let messageString = "";
		// Object.keys(defaultPortionsNotFulfilled).forEach((ingredientId:any, index:any, array:any) => {
		// 	if(messageString.length) messageString += ', ';
		// 	messageString += `${defaultPortionsNotFulfilled[ingredientId]}`;
		// 	if(index === (array.length - 1)) messageString += '.';
		// })

		messageString = preText + messageString;

		return (
			<ModalOverlay>
				<Modal>
					<ModalHeader>Gotta Sub!</ModalHeader>
					<ModalSubtitle>{messageString}</ModalSubtitle>
					<ModalNote>#IamChefofMyBowl</ModalNote>
					<ModalButtonGroup>
						<ModalSecondaryButton
							onClick={() => {
								navigate.push(`/kiosk/${kioskId}`);
							}}>
							CANCEL
						</ModalSecondaryButton>
						<ModalPrimaryButton onClick={() => setMiscDishData({ showDefaultNotSatisfiedMessage: false })}>OK</ModalPrimaryButton>
					</ModalButtonGroup>
				</Modal>
			</ModalOverlay>
		);
	}

	return (
		<Page padding={"0"} alignItems="" ref={pageRef}>
			<PageHeader>
				<BackButton onClick={() => history.goBack()}>
					<BackButtonImg src={chevronBackOutline} />
				</BackButton>
				<PageTitle>Customize Your Dish</PageTitle>
			</PageHeader>
			{!error && !loading && (
				<>
					{renderDishDetails()}
					<SegmentsSection>{renderSegments()}</SegmentsSection>
					<BottomCTA
						disabled={
							!allSegmentsFulfilled || mode === DISH_MODES.EXPLORE || pricingFailure || actionLoading || priceBeingFetched
						}
						onClick={handleAddToCartClick}>
						{actionLoading ? <IonSpinner name="dots" color="white" /> : mode === DISH_MODES.CUSTOMIZE ? "Save" : "Add to Cart"}
					</BottomCTA>
				</>
			)}
			{loading && (
				<Center>
					<IonSpinner name="dots" color={"#F2F2F2"}></IonSpinner>
				</Center>
			)}
			{actionLoading ? <ModalOverlay /> : ""}
			{error && !loading && (
				<Center>
					<Header>Something Went Wrong</Header>
				</Center>
			)}
			{/* {showSuccessModal && (
				<ModalOverlay>
					<Modal>
						<Header>Hurray!!!</Header>
						<ModalSubtitle>
							{dishData.title} has been added to your cart.
						</ModalSubtitle>
						<ModalButtonGroup>
							<ModalSecondaryButton
								onClick={() => {
									toggleSuccessModal(false);
									navigate.push(`/kiosk/${xookId}/cart`, "forward");
								}}>
								CHECKOUT
							</ModalSecondaryButton>
							<ModalPrimaryButton
								onClick={() => {
									toggleSuccessModal(false);
									navigate.push(`/kiosk/${xookId}`);
								}}>
								BROWSE MORE
							</ModalPrimaryButton>
						</ModalButtonGroup>
					</Modal>
				</ModalOverlay>
			)} */}
			{showErrorModal && (
				<ModalOverlay>
					<Modal>
						<ModalHeader>Oops!!!</ModalHeader>
						<ModalSubtitle>Failed to add {dishData.title} to your cart.</ModalSubtitle>
						<ModalButtonGroup>
							<ModalSecondaryButton
								onClick={() => {
									toggleErrorModal(false);
									addToCart();
								}}>
								TRY AGAIN
							</ModalSecondaryButton>
							<ModalPrimaryButton onClick={() => toggleErrorModal(false)}>CANCEL</ModalPrimaryButton>
						</ModalButtonGroup>
					</Modal>
				</ModalOverlay>
			)}
			{showAllergicModal && (
				<ModalOverlay>
					<Modal>
						<ModalHeader>Hey!</ModalHeader>
						<ModalSubtitle>
							You have selected{" "}
							{selectedAllergicIngredients && selectedAllergicIngredients.map((title: any) => <span>{title}, </span>)} that you
							are allergic to, do you still wanna go ahead?
						</ModalSubtitle>
						<ModalButtonGroup>
							<ModalSecondaryButton onClick={() => toggleAllergicModal(false)}>CANCEL</ModalSecondaryButton>
							<ModalPrimaryButton
								onClick={() => {
									toggleAllergicModal(false);
									addToCart();
								}}>
								CONTINUE
							</ModalPrimaryButton>
						</ModalButtonGroup>
					</Modal>
				</ModalOverlay>
			)}
			{showDefaultNotSatisfiedMessage && renderDefaultNotSatisfiedModal()}
		</Page>
	);
};

//Redux mapping
const mapStateToProps = (state: any) => {
	const { Xook, Dish, Food, User, Cart, Inventory } = state;
	return { Xook, Food, Dish, User, Cart, Inventory };
};

const mapDispatchToProps = (dispatch: any) => ({
	...bindActionCreators(
		{
			...DishActions,
			setCart,
			updateIngrdientQuantity,
		},
		dispatch
	),
});

export default connect(mapStateToProps, mapDispatchToProps)(withNav(DishCustomizations));
